/**
 * usePageMeta Hook
 *
 * Sets document.title, <meta name="description"> and (for public routes) the
 * <link rel="canonical"> on mount. Restores the original values on unmount so SPA
 * navigation doesn't leave stale meta tags from a previous page.
 *
 * Business rules (PBI #1270):
 * - Every indexable public page passes its `PUBLIC_PAGE_META` entry, which includes
 *   `canonicalPath`. Without this, the SPA shell's static canonical (the site root)
 *   would apply to every route once JavaScript runs, telling Google that /cards,
 *   /pricing, etc. are duplicates of the homepage.
 * - Open Graph / Twitter tags are intentionally NOT managed here: social unfurlers
 *   and most AI crawlers never execute JavaScript, so those tags are rewritten per
 *   route in the served HTML at build time (plugins/publicCrawlHtml.ts).
 *
 * Optionally injects <meta name="robots" content="noindex"> for pages that
 * must not be organically indexed (e.g. paid A/B-test landing-page variants,
 * which would otherwise compete with the control page and let organic
 * traffic pollute the test arms). The tag is removed on unmount.
 */

import { useEffect } from 'react';
import { buildPublicAbsoluteUrl } from '../utils/publicSiteUrl';

interface PageMeta {
  /** Page title — sets document.title */
  title: string;
  /** Meta description — updates or creates <meta name="description"> */
  description: string;
  /**
   * Root-relative canonical path ('/', '/cards', '/flowers/flp-123'). When set, the
   * <link rel="canonical"> href becomes the absolute public URL for this page.
   * Omit on authenticated / admin pages, which are not indexable.
   */
  canonicalPath?: string;
  /** When true, adds <meta name="robots" content="noindex"> for this page only. */
  noindex?: boolean;
}

/**
 * Sets per-page SEO meta tags when the component mounts.
 * Restores previous values on unmount for clean SPA navigation.
 */
export const usePageMeta = ({ title, description, canonicalPath, noindex = false }: PageMeta) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    let metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = metaDescription?.content ?? '';

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description;

    // Canonical — upsert the single <link rel="canonical"> the SPA shell ships with.
    let canonicalLink: HTMLLinkElement | null = null;
    let previousCanonicalHref = '';
    let createdCanonical = false;
    if (canonicalPath) {
      canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      previousCanonicalHref = canonicalLink?.getAttribute('href') ?? '';
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
        createdCanonical = true;
      }
      canonicalLink.setAttribute('href', buildPublicAbsoluteUrl(canonicalPath));
    }

    // Per-page robots directive — only created when requested, removed on unmount
    // so SPA navigation never leaks noindex onto indexable pages.
    let robotsMeta: HTMLMetaElement | null = null;
    if (noindex) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.content = 'noindex';
      document.head.appendChild(robotsMeta);
    }

    return () => {
      document.title = previousTitle;
      const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (meta) {
        meta.content = previousDescription;
      }
      if (canonicalLink) {
        if (createdCanonical) {
          canonicalLink.remove();
        } else {
          canonicalLink.setAttribute('href', previousCanonicalHref);
        }
      }
      robotsMeta?.remove();
    };
  }, [title, description, canonicalPath, noindex]);
};
